/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/QaChecklistSectionHeader.tsx
 * FILE_ID: JOBHUB-QA-SECTION-HEADER-001
 * PURPOSE: Sticky Area > Checklist heading with collapse and inline rename.
 * LANDMINE: sticky top-0 inside QA_PANE_SCROLL_CLASS. Opaque bg. Never
 *      absolute/fixed — a transparent header over a neighbour is the bug.
 *      Inside the Build table the sticky element is the group td
 *      (QaChecklistGroupRow), not this div. Its scroller must have no
 *      padding, or content shows above the pinned heading.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { ChevronDown, FileCheck2 } from 'lucide-react'
import { cn } from '@quotech/tenant-shared/lib/utils'
import { InlineRenameInput } from '../../qa/InlineRenameInput'

export function QaChecklistSectionHeader({
  title,
  done,
  total,
  expanded,
  onToggle,
  selected,
  onToggleSelected,
  onRename,
  renaming = false,
  onOpenItp,
  startRename = false,
  onRenameStarted,
  stickyClass = 'top-0',
  detail,
  drag,
}: {
  title: string
  done: number
  total: number
  expanded: boolean
  onToggle: () => void
  selected?: boolean
  onToggleSelected?: () => void
  onRename?: (title: string) => void
  renaming?: boolean
  onOpenItp?: () => void
  startRename?: boolean
  onRenameStarted?: () => void
  stickyClass?: string
  detail?: string
  /** Optional grip. Omitted callers keep the heading markup they have today. */
  drag?: ReactNode
}) {
  const [editing, setEditing] = useState(false)
  useEffect(() => {
    if (!startRename) return
    setEditing(true)
    onRenameStarted?.()
  }, [startRename, onRenameStarted])

  return (
    <div className={cn('sticky z-[1] flex items-center gap-1.5 border-b border-border bg-muted px-2 py-1.5', stickyClass)}>
      {onToggleSelected && (
        <input
          type="checkbox"
          checked={Boolean(selected)}
          onChange={onToggleSelected}
          aria-label={`Select ${title}`}
          className="h-3.5 w-3.5 shrink-0 rounded border-border"
        />
      )}
      {drag}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        aria-label={expanded ? `Collapse ${title}` : `Expand ${title}`}
        className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-foreground"
      >
        <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', !expanded && '-rotate-90')} aria-hidden />
      </button>
      {editing && onRename ? (
        <InlineRenameInput
          initialValue={title}
          busy={renaming}
          ariaLabel="Rename checklist"
          className="min-w-0 flex-1"
          onSubmit={(next) => {
            onRename(next)
            setEditing(false)
          }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <button
          type="button"
          onClick={onRename ? () => setEditing(true) : undefined}
          className="min-w-0 flex-1 truncate text-left text-xs font-semibold text-foreground"
        >
          {title}
          <span className="ml-1.5 font-normal tabular-nums text-muted-foreground">
            {done}/{total}
          </span>
        </button>
      )}
      {detail ? (
        <span className="min-w-0 truncate text-[11px] text-muted-foreground">{detail}</span>
      ) : null}
      {onOpenItp && (
        <button
          type="button"
          onClick={onOpenItp}
          title="Open ITP for this checklist"
          aria-label={`Open ITP for ${title}`}
          className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-primary"
        >
          <FileCheck2 className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
    </div>
  )
}
